import { useState } from "react";
import { useTranslation } from "../../context/TranslationContext";
import Alert from "../ds/Alert";
import Badge from "../ds/Badge";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import IconButton from "../ds/IconButton";
import InstagramVerificationModal from "../InstagramVerificationModal";
import useSocialAccounts from "../../hooks/useSocialAccounts";
import ConnectAccountModal from "./ConnectAccountModal";
import { APP_TINTED, PlatformTile, handleFromLink } from "./shared";

/**
 * Figma V5 / V6.5: the accounts a creator posts from, one row per platform. Linked
 * ones show their handle and can be removed; Instagram also has to be verified.
 */
const ConnectedAccounts = ({ platforms, intro, help, setToast, isDark }) => {
  const { t } = useTranslation();
  const { linkOf, stateOf, saveLink, saving, refresh } = useSocialAccounts(setToast);
  const [connecting, setConnecting] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const connect = async (value) => {
    const platform = connecting;
    if (!(await saveLink(platform, value))) return;
    setConnecting(null);
    if (platform.id === "instagram") setVerifying(true);
  };

  const remove = async () => {
    await saveLink(removing, "");
    setRemoving(null);
  };

  return (
    <section className="flex flex-col gap-space-16">
      {intro && <p className="text-subheadline text-label-secondary">{intro}</p>}
      <ul className="flex flex-col rounded-radius-lg bg-bg-raised py-space-8">
        {platforms.map((platform, index) => {
          const state = stateOf(platform.id);
          return (
            <li
              key={platform.id}
              className={`flex items-center gap-space-12 px-space-16 py-space-16 min-[600px]:px-space-20 ${
                index ? "border-t border-separator-default" : ""
              }`}
            >
              <PlatformTile platform={platform} />
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="truncate text-subheadline-semibold text-label-primary">{platform.name}</p>
                <p className="truncate text-footnote text-label-secondary">
                  {state === "missing"
                    ? t("campaignFlow.connectToSubmit", { format: platform.format })
                    : handleFromLink(linkOf(platform.id))}
                </p>
              </div>
              {state === "missing" ? (
                <Button
                  size="s"
                  variant="tinted"
                  className={APP_TINTED}
                  icon={<Icon name="plus" size={16} />}
                  onClick={() => setConnecting(platform)}
                >
                  {t("campaignFlow.connect")}
                </Button>
              ) : (
                <>
                  {state === "ready" ? (
                    <Badge tone="success">{t("campaignFlow.connected")}</Badge>
                  ) : (
                    <Button size="s" variant="tinted" className={APP_TINTED} onClick={() => setVerifying(true)}>
                      {t("campaignFlow.verify")}
                    </Button>
                  )}
                  <IconButton label={t("campaignFlow.disconnect")} variant="plain" onClick={() => setRemoving(platform)}>
                    <Icon name="close" size={18} />
                  </IconButton>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {help && <p className="text-footnote text-label-tertiary">{help}</p>}

      <ConnectAccountModal platform={connecting} saving={saving} onSave={connect} onClose={() => setConnecting(null)} />
      <Alert
        open={Boolean(removing)}
        title={removing ? t("campaignFlow.disconnectQuestion", { platform: removing.name }) : ""}
        message={t("campaignFlow.disconnectBody")}
        confirmLabel={t("campaignFlow.disconnect")}
        cancelLabel={t("common.cancel")}
        loading={saving}
        onConfirm={remove}
        onClose={() => setRemoving(null)}
      />
      <InstagramVerificationModal
        isOpen={verifying}
        setIsOpen={(open) => {
          setVerifying(open);
          if (!open) refresh();
        }}
        setToast={setToast}
        isDark={isDark}
        initialUsername={handleFromLink(linkOf("instagram")).replace(/^@/, "")}
        onVerificationUpdate={(verified) => {
          if (!verified) return;
          refresh();
          setToast?.(t("toasts.instagramVerifiedAccount"));
        }}
      />
    </section>
  );
};

export default ConnectedAccounts;
